import React, { useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import style from "../common.module.scss";
import DocumentNewIcon from "../../../shared/icons/DocumentNewIcon";
import EyeIcon from "../../../shared/icons/EyeIcon";
import UnloadIcon from "../../../shared/icons/UnloadIcon";
import DeleteNewIcon from "../../../shared/icons/DeleteNewIcon";
import Loading from "../../../shared/Loading/Loading";
import { File } from "../../../../services";
import { toast } from "react-toastify";
import moment from "moment";
import ArrowDownIcon from "../../../shared/icons/ArrowDownIcon";
import CustomButton from "../../../custom/custom-button/CustomButton";
import PhotoViewer from "../../../partners/components/PhotoViewer";
import { useSelector } from "react-redux";
import { useScreenSize } from "../../../../hooks/useScreenSize";

const ListFilesOrder = ({ documents = [], refreshDocuments, orderStatus }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const { isMobileView } = useScreenSize();
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [isOpenViewer, setIsOpenViewer] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [currentFileId, setCurrentFileId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); 
  const [cachedFiles, setCachedFiles] = useState({});

  const setLoadingViewer = (id, val) => setLoadingStates((p) => ({ ...p, [id]: val }));
  const setLoadingDownload = (id, val) => setLoadingStatesDownload((p) => ({ ...p, [id]: val }));

  const isPdf = (doc) => {
    const name = doc?.fileName?.toLowerCase() || "";
    return doc?.contentType === "application/pdf" || name.endsWith(".pdf");
  };

  const openFile = (doc) => {
    if (!doc) {
      toast.error(t("orderInfo.listFilesOrder.fileNotFound"));
      return;
    }
    const cache = cachedFiles[doc.fileId];
    if (isPdf(doc)) {
      if (cache) {
        window.open(cache, "_blank");
        return;
      }
      setLoadingViewer(doc.id, true);
      File.downloadFileById(doc.fileId)
        .then((resp) => {
          const blob = new Blob([resp.data], { type: resp.data.type || "application/pdf" });
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setCachedFiles((prev) => ({ ...prev, [doc.fileId]: url }));
        })
        .catch(() => toast.error(t("orderInfo.listFilesOrder.openFileError")))
        .finally(() => setLoadingViewer(doc.id, false));
      return;
    }
    if (cache) {
      setViewerUrl(cache);
      setCurrentFileId(doc.fileId);
      setIsOpenViewer(true);
      return;
    }
    setLoadingViewer(doc.id, true);
    File.downloadFileById(doc.fileId)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: resp.data.type || doc.contentType });
        const url = URL.createObjectURL(blob);
        setViewerUrl(url);
        setCurrentFileId(doc.fileId);
        setIsOpenViewer(true);
        setCachedFiles((prev) => ({ ...prev, [doc.fileId]: url }));
      })
      .catch(() => toast.error(t("orderInfo.listFilesOrder.openFileError")))
      .finally(() => setLoadingViewer(doc.id, false));
  };

  const downloadFile = (doc) => {
    if (!doc) {
      toast.error(t("orderInfo.listFilesOrder.fileNotFound"));
      return;
    }
    const cache = cachedFiles[doc.fileId];
    setLoadingDownload(doc.id, true);
    if (cache) {
      const a = document.createElement("a");
      a.href = cache;
      a.setAttribute("download", doc.fileName || "file");
      a.click();
      setLoadingDownload(doc.id, false);
      return;
    }
    File.downloadFileById(doc.fileId)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: resp.data.type || doc.contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", doc.fileName || "file");
        a.click();
        setCachedFiles((prev) => ({ ...prev, [doc.fileId]: url }));
      })
      .catch(() => toast.error(t("orderInfo.listFilesOrder.downloadFileError")))
      .finally(() => setLoadingDownload(doc.id, false));
  };

  const openDeleteModal = (fileId) => {
    setDeletingId(fileId);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingId) return;
    setIsDeleting(true);
    File.deleteDocumentOrders(deletingId)
      .then(() => {
        toast.success(t("orderInfo.listFilesOrder.deleteSuccess"));
        refreshDocuments && refreshDocuments();
      })
      .catch(() => toast.error(t("orderInfo.listFilesOrder.deleteError")))
      .finally(() => {
        setIsDeleting(false);
        setIsConfirmOpen(false);
        setDeletingId(null);
      });
  };

  const mainDocs = documents.filter((d) => d.documentType === 5);
  const additionalDocs = documents.filter((d) => d.documentType === 5 || d.documentType === 7);

  const renderList = (docs) => {
    const displayed = showAll || docs.length <= 10 ? docs : docs.slice(0, 10);
    return (
      <div className={style.documentsList}>
        {displayed.map((doc) => (
          <div key={doc.id} className={style.documentItem}>
            <div className={style.documentItem_icon}><DocumentNewIcon /></div>
            <div className={style.documentItem_content}>
              <div
                className={cn(
                  style.documentItem_name,
                  isMobileView ? style.documentItem_name_mobile : style.documentItem_name_desktop,
                )}
              >
                {doc.fileName || t("orderInfo.listFilesOrder.defaultFileName")}
              </div>
              <div className={style.documentItem_size}>
                {moment(doc.createdAt).format("DD.MM.YYYY HH:mm")} {" "} от {doc.companyName}
              </div>
            </div>
            {orderStatus !== "completed" && user?.companyId === doc.companyId && (
              <DeleteNewIcon style={{ cursor: "pointer" }} onClick={() => openDeleteModal(doc.fileId)} />
            )}
            {loadingStatesDownload[doc.id] ? <Loading inBlock marginBottom={0} /> : (
              <UnloadIcon style={{ cursor: "pointer" }} onClick={() => downloadFile(doc)} />
            )}
            {loadingStates[doc.id] ? <Loading inBlock marginBottom={0} /> : (
              <EyeIcon style={{ cursor: "pointer" }} onClick={() => openFile(doc)} />
            )}
          </div>
        ))}
        {docs.length > 10 && (
          <div
            className={style.showMoreDocuments}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? t("orderInfo.listFilesOrder.hide") : t("orderInfo.listFilesOrder.showMore", { count: docs.length - 10 })}
            <ArrowDownIcon style={{ transform: showAll ? "rotate(180deg)" : "rotate(0deg)", marginLeft: 5 }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={style.cargo_wrapper}>
      {/* {mainDocs.length > 0 && (
        <>
          <div>{t("orderInfo.orderDecisionAviation.mainDocsTitle")}</div>
          {renderList(mainDocs)}
        </>
      )*/}
      <div>{t("orderInfo.uploadFile.uploadAdditionalDocsTitle")}</div> 
      {additionalDocs.length > 0 && renderList(additionalDocs)}

      {isOpenViewer && (
        <PhotoViewer
          imgUrl={viewerUrl}
          onClose={() => setIsOpenViewer(false)}
          onDownload={() => {
            const doc = documents.find((d) => d.fileId === currentFileId);
            downloadFile(doc);
          }}
          ShowBtnDownload
        />
      )}
      {isConfirmOpen && (
        <div className={style.confirmModalOverlay}>
          <div className={style.confirmModal}>
            <p>{t("orderInfo.listFilesOrder.confirmDelete")}</p>
            <div className={style.confirmModal_actions}>
              <CustomButton
                isWhite
                text={t("common.cancel")}
                onClick={() => { setDeletingId(null); setIsConfirmOpen(false); }}
              />
              <CustomButton
                text={t("common.confirm")}
                onClick={handleConfirmDelete}
                isLoading={isDeleting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListFilesOrder;
